/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { Checkbox, Chip, FormControlLabel, Grid, Tooltip } from "@mui/material";
import {
  BookOpen,
  FileSearch,
  FolderOpen,
  MagnifyingGlass,
  Microphone,
  Play,
  Question,
} from "phosphor-react";
import ReactSearchBox from "react-search-box";
import Swal from "sweetalert2";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiDatePicker from "components/SuiDatePicker";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import Alert from "custom/Alert";
import Footer from "custom/Footer";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import MessageTypes from "enums/message-types";
import { removeMessage } from "redux/actions/message";
import {
  BULK_ASSIGNMENT,
  startAddOrEditAssignment,
  getQuizListOfBook,
  getMaterialListOfBook,
  getQuizDetail,
  getBulkAssignmentClasses,
  previewBulkAssignment,
  saveBulkAssignment,
  clearBulkAssignment,
} from "redux/actions/pages";
import QuizPreviewDialog from "layouts/assignment/components/QuizPreviewDialog";
import "layouts/assignment/components/AddOrEditAssignment.js/_add-or-edit-assignment.css";

const initialSelectedTaskTypes = {
  reading: false,
  listening: false,
  recording: false,
  quiz: false,
  material: false,
};

// PREP, GRADE_1, GRADE_2, ... sırası; bilinmeyenler sona alfabetik
const levelRank = (level) => {
  if (level === "PREP") return -1;
  const match = /^GRADE_(\d+)$/.exec(level);
  return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};
const compareLevels = (a, b) => {
  const diff = levelRank(a) - levelRank(b);
  if (diff !== 0) return diff;
  return String(a).localeCompare(String(b));
};

const formatDate = (date) =>
  `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}.${
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  }.${date.getFullYear()}`;

const BulkAssignment = ({
  allBooks,
  localizations,
  classes,
  bookQuizList,
  bookMaterialList,
  previewQuiz,
  preview,
  results,
  apiErrorMsg,
  startPage,
  getClasses,
  getQuizList,
  getMaterialList,
  getPreviewQuiz,
  requestPreview,
  saveBulk,
  clearBulk,
  removeApiErrorMsg,
}) => {
  const history = useHistory();
  const l = localizations ?? {};

  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedCampusIds, setSelectedCampusIds] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState(0);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState(initialSelectedTaskTypes);
  const [selectedQuizIds, setSelectedQuizIds] = useState([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
  const [termin, setTermin] = useState(null);
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [previewQuizId, setPreviewQuizId] = useState(null);

  const awaitingPreview = useRef(false);
  const lastAssignment = useRef(null);

  // ---- türetilmiş veriler ----
  const levels = useMemo(
    () => [...new Set(classes.map((c) => c.classLevel))].sort(compareLevels),
    [classes]
  );
  const campuses = useMemo(() => {
    const map = new Map();
    classes.forEach((c) => {
      if (!map.has(c.suborganizationId)) map.set(c.suborganizationId, c.suborganizationKey);
    });
    return [...map.entries()].map(([id, key]) => ({ id, key }));
  }, [classes]);

  const matchesFilter = (c, levelList, campusList) =>
    levelList.includes(c.classLevel) && campusList.includes(c.suborganizationId);

  const matchingClasses = useMemo(
    () => classes.filter((c) => matchesFilter(c, selectedLevels, selectedCampusIds)),
    [classes, selectedLevels, selectedCampusIds]
  );
  const groupedByCampus = useMemo(
    () =>
      campuses
        .map((campus) => ({
          ...campus,
          classes: matchingClasses.filter((c) => c.suborganizationId === campus.id),
        }))
        .filter((g) => g.classes.length > 0),
    [campuses, matchingClasses]
  );
  const chosenClasses = matchingClasses.filter((c) => selectedClassIds.includes(c.id));
  const totalStudents = chosenClasses.reduce((sum, c) => sum + (c.studentCount ?? 0), 0);

  // ---- yaşam döngüsü ----
  useEffect(() => {
    startPage();
    getClasses();
    return () => clearBulk();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Sınıflar gelince tüm kampüsler varsayılan olarak seçili
    setSelectedCampusIds(campuses.map((c) => c.id));
    // eslint-disable-next-line
  }, [classes]);

  useEffect(() => {
    if (allBooks.length > 0 && selectedBookId > 0) {
      const book = allBooks.find((b) => b.id === selectedBookId);
      if (book) setSelectedBook(book);
    }
  }, [selectedBookId, allBooks]);

  useEffect(() => {
    if (selectedBookId > 0) {
      setSelectedTaskTypes(initialSelectedTaskTypes);
      setSelectedQuizIds([]);
      setSelectedMaterialIds([]);
      if (!bookQuizList || bookQuizList.bookId !== selectedBookId) {
        getQuizList({ bookId: selectedBookId });
      }
      if (!bookMaterialList || bookMaterialList.bookId !== selectedBookId) {
        getMaterialList({ bookId: selectedBookId });
      }
    }
    // eslint-disable-next-line
  }, [selectedBookId]);

  useEffect(() => {
    if (previewQuizId && previewQuizId > 0) {
      getPreviewQuiz({ quizId: previewQuizId });
    }
    // eslint-disable-next-line
  }, [previewQuizId]);

  // Hata mesajı
  useEffect(() => {
    if (apiErrorMsg && apiErrorMsg.messageContent) {
      awaitingPreview.current = false;
      Swal.fire({
        icon: apiErrorMsg.messageType || "error",
        title:
          apiErrorMsg.messageType === MessageTypes.success
            ? l.MESSAGE_SUCCESS ?? "BAŞARILI!"
            : l.MESSAGE_ERROR ?? "HATA!",
        text: apiErrorMsg.messageContent,
        showCancelButton: false,
        confirmButtonText: l.MESSAGE_CONFIRM_BTN ?? "Tamam",
      }).then(() => {
        removeApiErrorMsg(BULK_ASSIGNMENT);
      });
    }
    // eslint-disable-next-line
  }, [apiErrorMsg]);

  // Önizleme geldiğinde onay penceresi
  useEffect(() => {
    if (!preview || !awaitingPreview.current) return;
    awaitingPreview.current = false;
    const assignment = lastAssignment.current;
    Swal.fire({
      icon: "warning",
      title: l.BULK_ASSIGNMENT_CONFIRM_TITLE ?? "Toplu Görev Gönderimi",
      text: `${preview.classCount} sınıfa, ${preview.studentCount} öğrenciye gönderilecek. ${preview.studentsWithProgress} öğrencinin bu kitaptaki ilerlemesi (okuma/dinleme konumu, quiz sonuçları, ses kayıtları) sıfırlanacak. Devam edilsin mi?`,
      showCancelButton: true,
      confirmButtonText: l.BULK_ASSIGNMENT_CONFIRM_BTN ?? "Evet, gönder",
      cancelButtonText: l.BULK_ASSIGNMENT_CANCEL_BTN ?? "Vazgeç",
    }).then((result) => {
      if (result.isConfirmed) {
        saveBulk({ assignment });
      } else {
        clearBulk();
      }
    });
    // eslint-disable-next-line
  }, [preview]);

  // ---- seçim işleyicileri ----
  const applyFilter = (levelList, campusList) => {
    setSelectedLevels(levelList);
    setSelectedCampusIds(campusList);
    setSelectedClassIds(
      classes.filter((c) => matchesFilter(c, levelList, campusList)).map((c) => c.id)
    );
  };

  const toggleLevel = (level) => {
    const next = selectedLevels.includes(level)
      ? selectedLevels.filter((x) => x !== level)
      : [...selectedLevels, level];
    applyFilter(next, selectedCampusIds);
  };

  const toggleCampus = (campusId) => {
    const next = selectedCampusIds.includes(campusId)
      ? selectedCampusIds.filter((x) => x !== campusId)
      : [...selectedCampusIds, campusId];
    applyFilter(selectedLevels, next);
  };

  const toggleClass = (classId) => {
    setSelectedClassIds(
      selectedClassIds.includes(classId)
        ? selectedClassIds.filter((x) => x !== classId)
        : [...selectedClassIds, classId]
    );
  };

  const toggleTaskType = (key) =>
    setSelectedTaskTypes({ ...selectedTaskTypes, [key]: !selectedTaskTypes[key] });

  const toggleId = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const showErrors = (errorMessage) =>
    Swal.fire({
      icon: "error",
      title: l.MISSING_INFO_ERROR_TITLE ?? "Eksik Bilgi",
      html: `
        <p class="error-desc">
          ${
            l.MISSING_INFO_ERROR_DESC ??
            "Zorunlu alan girişleri eksik yapıldı. Lütfen aşağıda belirtilen eksiklikleri gidererek tekrar deneyiniz."
          }
        </p>
        <ul class="message-error-list">
          ${errorMessage.map((m) => `<li class="message-error-item">* ${m}</li>`).join("")}
        </ul>
      `,
      showCancelButton: false,
      confirmButtonText: l.MESSAGE_CONFIRM_BTN ?? "Tamam",
    });

  const onSend = (e) => {
    e.preventDefault();
    const errorMessage = [];
    const { reading, listening, recording, quiz, material } = selectedTaskTypes;

    if (!selectedBookId || selectedBookId < 1) {
      errorMessage.push(l.BOOK_NOT_SELECTED_ERROR ?? "Kitap seçimi yapmak zorundasınız.");
    }
    if (!reading && !listening && !recording && !quiz && !material) {
      errorMessage.push(l.TASK_TYPE_NOT_SELECTED_ERROR ?? "En az 1 görev tipi seçiniz.");
    }
    if (quiz && selectedQuizIds.length === 0) {
      errorMessage.push(
        l.QUIZ_NOT_SELECTED_ERROR ?? "Quiz görev tipi seçildiğinden en az 1 adet quiz seçilmelidir."
      );
    }
    if (material && selectedMaterialIds.length === 0) {
      errorMessage.push(
        l.MATERIAL_NOT_SELECTED_ERROR ??
          "Materyal görev tipi seçildiğinden en az 1 adet materyal seçilmelidir."
      );
    }
    if (chosenClasses.length === 0) {
      errorMessage.push(
        l.BULK_CLASS_NOT_SELECTED_ERROR ??
          "Görev en az 1 sınıfa atanmalıdır. Lütfen en az 1 sınıf seçiniz."
      );
    }
    if (!termin || termin === "") {
      errorMessage.push(l.DUE_DATE_NOT_PICKED_ERROR ?? "Görev bitiş tarihi seçiniz.");
    }

    if (errorMessage.length > 0) {
      showErrors(errorMessage);
      return;
    }

    const tasks = [];
    if (reading) tasks.push({ taskType: "READ" });
    if (listening) tasks.push({ taskType: "LISTEN" });
    if (recording) tasks.push({ taskType: "RECORD" });
    if (quiz) selectedQuizIds.forEach((id) => tasks.push({ taskType: "QUIZ", elementId: id }));
    if (material) {
      selectedMaterialIds.forEach((id) => tasks.push({ taskType: "MATERIAL", elementId: id }));
    }

    const classIds = chosenClasses.map((c) => c.id);
    lastAssignment.current = {
      classIds,
      bookId: selectedBookId,
      listen: listening,
      read: reading,
      record: recording,
      quiz,
      material,
      message,
      audioMessageUrl: "",
      dueDate: termin,
      tasks,
    };
    awaitingPreview.current = true;
    requestPreview({ classIds, bookId: selectedBookId });
  };

  // ---- görünüm yardımcıları ----
  const campusLabel = (key) => l[key] ?? key;
  const levelLabel = (level) => l[level] ?? level;

  const taskButton = (key, title, Icon, color, disabled = false, tip = null) => (
    <div className="checkbox-group">
      <Tooltip title={tip ?? title} placement="bottom">
        <span>
          <SuiButton
            onClick={(e) => {
              e.preventDefault();
              toggleTaskType(key);
            }}
            className={`checkbox-label-icon ${selectedTaskTypes[key] ? color : "not-selected"}`}
            size="large"
            iconOnly
            circular
            disabled={disabled}
          >
            <Icon weight="duotone" color="white" />
          </SuiButton>
        </span>
      </Tooltip>
    </div>
  );

  const hasQuizzes =
    bookQuizList &&
    bookQuizList.bookId > 0 &&
    bookQuizList.quizes &&
    Object.keys(bookQuizList.quizes).length > 0;
  const hasMaterials =
    bookMaterialList &&
    bookMaterialList.bookId > 0 &&
    bookMaterialList.materials &&
    Object.keys(bookMaterialList.materials).length > 0;

  const succeeded = results ? results.filter((r) => r.assignmentId && !r.error) : [];
  const failed = results ? results.filter((r) => !r.assignmentId || r.error) : [];

  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={8}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <h3>{l.BULK_ASSIGNMENT_HEADER ?? "Toplu Görev"}</h3>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {l.BULK_ASSIGNMENT_DESC ??
                  "Seçtiğiniz sınıf seviyelerindeki tüm kampüslerin sınıflarına aynı görevi gönderin. Her sınıf için ayrı bir görev oluşturulur."}
              </SuiTypography>
            </Grid>

            {results ? (
              <Grid item xs={12}>
                <h3 className="assignment-sub-header">
                  {l.BULK_ASSIGNMENT_RESULT_HEADER ?? "Gönderim Sonucu"}
                </h3>
                <div style={{ margin: "0 10px" }}>
                  <SuiTypography variant="h6" color={failed.length > 0 ? "warning" : "success"}>
                    {`${succeeded.length} sınıf için görev oluşturuldu${
                      failed.length > 0 ? `, ${failed.length} sınıf için oluşturulamadı` : ""
                    }.`}
                  </SuiTypography>
                  {failed.length > 0 ? (
                    <ul className="message-error-list" style={{ marginTop: "12px" }}>
                      {failed.map((r) => (
                        <li className="message-error-item" key={`failed-${r.classId}`}>
                          {`${r.className} (${campusLabel(
                            (classes.find((c) => c.id === r.classId) ?? {}).suborganizationKey ??
                              r.suborganizationId
                          )}): ${r.error ?? "Bilinmeyen hata"}`}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div style={{ marginTop: "16px" }}>
                    <SuiButton
                      variant="gradient"
                      color="info"
                      style={{ marginRight: "16px" }}
                      onClick={(e) => {
                        e.preventDefault();
                        history.push("/assignment");
                      }}
                    >
                      {l.GO_TO_ASSIGNMENTS_BTN ?? "Görevlere Git"}
                    </SuiButton>
                    <SuiButton
                      variant="outlined"
                      color="info"
                      onClick={(e) => {
                        e.preventDefault();
                        clearBulk();
                      }}
                    >
                      {l.NEW_BULK_ASSIGNMENT_BTN ?? "Yeni Toplu Görev"}
                    </SuiButton>
                  </div>
                </div>
              </Grid>
            ) : (
              <>
                <Grid item lg={7} xs={12}>
                  <Grid container spacing={5}>
                    {/* Sınıf seviyesi + kampüs */}
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">
                        {l.BULK_GRADE_LEVELS ?? "Sınıf Seviyeleri"}
                      </h3>
                      <div style={{ margin: "0 10px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {levels.map((level) => (
                          <Chip
                            key={level}
                            label={levelLabel(level)}
                            clickable
                            color={selectedLevels.includes(level) ? "info" : "default"}
                            variant={selectedLevels.includes(level) ? "filled" : "outlined"}
                            onClick={() => toggleLevel(level)}
                          />
                        ))}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">{l.BULK_CAMPUSES ?? "Kampüsler"}</h3>
                      <div style={{ margin: "0 10px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {campuses.map((campus) => (
                          <Chip
                            key={campus.id}
                            label={campusLabel(campus.key)}
                            clickable
                            color={selectedCampusIds.includes(campus.id) ? "info" : "default"}
                            variant={selectedCampusIds.includes(campus.id) ? "filled" : "outlined"}
                            onClick={() => toggleCampus(campus.id)}
                          />
                        ))}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">{l.BULK_CLASSES ?? "Sınıflar"}</h3>
                      <div style={{ margin: "0 10px" }}>
                        {groupedByCampus.length === 0 ? (
                          <SuiTypography variant="button" color="text">
                            {l.BULK_NO_CLASSES ??
                              "Sınıf seviyesi seçtiğinizde sınıflar listelenir."}
                          </SuiTypography>
                        ) : (
                          groupedByCampus.map((group) => (
                            <div key={`campus-${group.id}`} style={{ marginBottom: "12px" }}>
                              <SuiTypography variant="h6">{campusLabel(group.key)}</SuiTypography>
                              {group.classes.map((c) => (
                                <div className="selection-item" key={`class-${c.id}`}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        color="secondary"
                                        checked={selectedClassIds.includes(c.id)}
                                        onChange={() => toggleClass(c.id)}
                                      />
                                    }
                                    label={`${c.className} (${c.studentCount ?? 0} ${
                                      l.STUDENT_LOWER ?? "öğrenci"
                                    })`}
                                  />
                                </div>
                              ))}
                            </div>
                          ))
                        )}
                        <SuiTypography variant="button" fontWeight="bold">
                          {`${chosenClasses.length} sınıf, ${totalStudents} öğrenci`}
                        </SuiTypography>
                      </div>
                    </Grid>

                    {/* Kitap */}
                    <Grid item xs={12}>
                      <Grid container flexWrap="wrap" alignItems="center">
                        <Grid item lg={6} xs={6}>
                          <h3 className="assignment-sub-header">{l.BOOK ?? "Kitap"}</h3>
                        </Grid>
                        <Grid item lg={6} xs={6} style={{ minWidth: "200px" }}>
                          <div className="searchbox-wrapper">
                            <div className="searchbox-container">
                              <ReactSearchBox
                                data={allBooks.map((book) => ({ key: book.id, value: book.title }))}
                                placeholder={
                                  l.ASSIGNMENT_SELECT_WITH_BOOK_PLACEHOLDER ??
                                  "Kitap adıyla seçiniz"
                                }
                                onSelect={(record) => {
                                  if (record.item?.key) setSelectedBookId(record.item.key);
                                }}
                                leftIcon={
                                  <MagnifyingGlass size={20} weight="duotone" color="#344767" />
                                }
                                iconBoxSize="40px"
                              />
                            </div>
                          </div>
                        </Grid>
                        {selectedBook ? (
                          <Grid item xs={12} lg={12} mt={3}>
                            <div className="book-detail-container">
                              <div className="book-cover">
                                <img src={selectedBook.coverImage} alt="" />
                              </div>
                              <div className="book-info">
                                <SuiTypography variant="h5">{selectedBook.title}</SuiTypography>
                                <div>
                                  <SuiTypography
                                    variant="button"
                                    fontWeight="light"
                                    fontStyle="italic"
                                  >
                                    {selectedBook.generalInfo}
                                  </SuiTypography>
                                </div>
                              </div>
                            </div>
                          </Grid>
                        ) : null}
                      </Grid>
                    </Grid>

                    {/* Görev türü */}
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">{l.TASK_TYPE ?? "Görev Türü"}</h3>
                      <div className="task-types-container" style={{ margin: "0 10px" }}>
                        {taskButton("reading", l.READING_TOOLTIP ?? "Okuma", BookOpen, "blue")}
                        {taskButton(
                          "listening",
                          l.LISTENING_TOOLTIP ?? "Dinleme",
                          Play,
                          "blue",
                          !!selectedBook && !selectedBook.hasBookAudio,
                          selectedBook && !selectedBook.hasBookAudio
                            ? l.BOOK_HAS_NO_AUDIO_TOOLTIP ?? "Dinleme görevi verilemez!"
                            : null
                        )}
                        {taskButton(
                          "recording",
                          l.RECORDING_TOOLTIP ?? "Oku - Kaydet",
                          Microphone,
                          "blue"
                        )}
                        {hasQuizzes
                          ? taskButton("quiz", l.QUIZ_TOOLTIP ?? "Quiz", Question, "purple")
                          : null}
                        {hasMaterials
                          ? taskButton(
                              "material",
                              l.MATERIAL_TOOLTIP ?? "Materyal",
                              FolderOpen,
                              "purple"
                            )
                          : null}
                      </div>
                    </Grid>

                    {selectedTaskTypes.quiz || selectedTaskTypes.material ? (
                      <Grid item xs={12}>
                        <Grid container>
                          {selectedTaskTypes.quiz ? (
                            <Grid item lg={6} xs={12}>
                              <h3 className="assignment-sub-header">
                                {l.SELECT_QUIZ ?? "Quiz Seç"}
                              </h3>
                              <div
                                className="quiz-selection-container"
                                style={{ margin: "0 20px" }}
                              >
                                {Object.values(bookQuizList?.quizes ?? {})
                                  .sort((a, b) => a.quizOrder - b.quizOrder)
                                  .map((quiz) => (
                                    <div className="selection-item" key={`quiz-${quiz.id}`}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            color="secondary"
                                            checked={selectedQuizIds.includes(quiz.id)}
                                            onChange={() =>
                                              toggleId(selectedQuizIds, setSelectedQuizIds, quiz.id)
                                            }
                                          />
                                        }
                                        label={
                                          <div style={{ display: "flex", alignItems: "center" }}>
                                            <p>{`${quiz.quizOrder}. ${
                                              l[quiz.type] ?? quiz.type
                                            }`}</p>
                                            <div
                                              style={{ marginLeft: "5px", cursor: "pointer" }}
                                              aria-hidden
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setPreviewQuizId(quiz.id);
                                                setModalOpen(true);
                                              }}
                                            >
                                              <Tooltip
                                                title={l.PREVIEW_TOOLTIP ?? "Dökümana Gözat"}
                                                placement="right"
                                              >
                                                <FileSearch
                                                  size={22}
                                                  weight="duotone"
                                                  color="#6C757D"
                                                />
                                              </Tooltip>
                                            </div>
                                          </div>
                                        }
                                      />
                                    </div>
                                  ))}
                              </div>
                            </Grid>
                          ) : null}
                          {selectedTaskTypes.material ? (
                            <Grid item lg={6} xs={12}>
                              <h3 className="assignment-sub-header">
                                {l.SELECT_MATERIAL ?? "Materyal Seç"}
                              </h3>
                              <div
                                className="material-selection-container"
                                style={{ margin: "0 20px" }}
                              >
                                {Object.values(bookMaterialList?.materials ?? {})
                                  .sort((a, b) => a.materialOrder - b.materialOrder)
                                  .map((material) => (
                                    <div className="selection-item" key={`material-${material.id}`}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            color="secondary"
                                            checked={selectedMaterialIds.includes(material.id)}
                                            onChange={() =>
                                              toggleId(
                                                selectedMaterialIds,
                                                setSelectedMaterialIds,
                                                material.id
                                              )
                                            }
                                          />
                                        }
                                        label={
                                          <div style={{ display: "flex", alignItems: "center" }}>
                                            <p>
                                              {`${material.materialOrder}. ${
                                                l[material.learningDesignType] ??
                                                material.learningDesignType
                                              }`}
                                            </p>
                                            <a
                                              href={material.fileUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                              style={{ marginLeft: "5px" }}
                                            >
                                              <Tooltip
                                                title={l.PREVIEW_TOOLTIP ?? "Dökümana Gözat"}
                                                placement="right"
                                              >
                                                <FileSearch
                                                  size={22}
                                                  weight="duotone"
                                                  color="#6C757D"
                                                />
                                              </Tooltip>
                                            </a>
                                          </div>
                                        }
                                      />
                                    </div>
                                  ))}
                              </div>
                            </Grid>
                          ) : null}
                        </Grid>
                      </Grid>
                    ) : null}

                    {/* Teslim tarihi */}
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">
                        {l.ASSINMENT_DUE_DATE ?? "Görev Teslim Tarihi"}
                      </h3>
                      <div className="due-date-container" style={{ margin: "0 10px" }}>
                        <SuiDatePicker
                          input={{ placeholder: l.PICK_DATE_PLACEHOLDER ?? "Tarih seçiniz" }}
                          options={{
                            locale: "TR".toLowerCase(),
                            dateFormat: "d.m.Y",
                            enableTime: false,
                            minDate: "today",
                          }}
                          value={termin}
                          onChange={([date]) => setTermin(formatDate(date))}
                        />
                      </div>
                    </Grid>

                    {/* Mesaj */}
                    <Grid item xs={12}>
                      <h3 className="assignment-sub-header">{l.MESSAGE ?? "Mesaj"}</h3>
                      <div className="message-container" style={{ margin: "0 10px" }}>
                        <SuiInput
                          placeholder={l.WRITE_MESSAGE_PLACEHOLDER ?? "Mesaj yazınız..."}
                          multiline
                          rows={5}
                          onChange={(e) => setMessage(e.target.value)}
                          value={message}
                        />
                        <SuiTypography variant="caption" color="text">
                          {l.BULK_MESSAGE_NOTE ?? 'Mesajlar "Okuma Platformu" adıyla gönderilir.'}
                        </SuiTypography>
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <SuiButton variant="gradient" color="info" onClick={onSend}>
                        {l.SEND_BTN ?? "Gönder"}
                      </SuiButton>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
          <QuizPreviewDialog
            open={modalOpen && previewQuiz !== null}
            onClose={() => setModalOpen(false)}
            quiz={previewQuiz}
          />
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
};

const mapStateToProps = (state) => ({
  allBooks: state.data.minimizedBooks ?? [],
  localizations: state.localization?.pairs,
  classes: state.data.bulkAssignmentClasses ?? [],
  bookQuizList: state.data.bookQuizList,
  bookMaterialList: state.data.bookMaterialList,
  previewQuiz: state.data.previewQuiz,
  preview: state.data.bulkAssignmentPreview,
  results: state.data.bulkAssignmentResults,
  apiErrorMsg: state.message[BULK_ASSIGNMENT],
});

const mapDispatchToProps = {
  startPage: startAddOrEditAssignment,
  getClasses: getBulkAssignmentClasses,
  getQuizList: getQuizListOfBook,
  getMaterialList: getMaterialListOfBook,
  getPreviewQuiz: getQuizDetail,
  requestPreview: previewBulkAssignment,
  saveBulk: saveBulkAssignment,
  clearBulk: clearBulkAssignment,
  removeApiErrorMsg: removeMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkAssignment);
